import axios, {AxiosInstance} from "axios";
import ApiError from "@/modules/Api/ApiError";
import {MeEntity, MeStatisticEntity} from "@/entities/UserEntity";
import {NotificationEntity} from "@/entities/NotificationEntity";
import DepositEntity from "@/entities/DepositEntity";
import WithdrawalEntity from "@/entities/WithdrawalEntity";
import BalanceHistoryEntity from "@/entities/BalanceHistoryEntity";
import TradeEntity from "@/entities/TradeEntity";

interface ApiErrorResponse
{
	status : false,
	error : string
}

interface ApiSuccessResponse<T>
{
	status : true,
	response : T
}

type ApiResponse<T> = ApiErrorResponse | ApiSuccessResponse<T>;

interface PaginationOrder{
	field : string,
	mode : string
}

interface PaginationFilters extends Record<string, any>
{

}

interface PaginationData<T>{
	items : T[],
	count : number,
	pages : number,
	current_page : number
}

export default class Api
{

	protected initData : string;
	protected axios : AxiosInstance;

	public constructor(initData : string, baseUrl = '') {
		this.initData = initData;
		this.axios = axios.create({
			baseURL : baseUrl + '/api/user',
			headers : {
				Authorization : 'Bearer ' + initData
			}
		});
	}

	protected async request<T>(path : string, params : Record<string, any> = {}) : Promise<T>
	{

		let response = await this.axios.post(path, params);
		let data : ApiResponse<T> = response.data;

		if(data.status){
			return data.response;
		}else{
			throw new ApiError(data.error);
		}

	}

	protected async paginationRequest<T>(
		path : string,
		page : number,
		limit : number = 30,
		order : PaginationOrder = {
			field : 'id',
			mode : 'asc'
		},
		filters : PaginationFilters = {}
	) : Promise<PaginationData<T>>
	{

		return this.request<PaginationData<T>>(path, {
			page : page,
			limit : limit,
			sort_mode : order.mode,
			sort_field : order.field,
			...filters
		})

	}

	public async getMe() : Promise<MeEntity>
	{
		return this.request<MeEntity>('/profile/me');
	}

	public async getNotifications(
		page : number,
		limit : number = 30,
		order : PaginationOrder = {
			field : 'id',
			mode : 'asc'
		}
	) : Promise<PaginationData<NotificationEntity>>{
		return this.paginationRequest<NotificationEntity>('/notifications/list-unread', page, limit, order);
	}

	public async readNotifications() : Promise<void>
	{
		await this.request<void>('/notifications/clear');
	}

	public async getDeposits(
		page : number,
		limit : number = 30,
		order : PaginationOrder = {
			field : 'id',
			mode : 'asc'
		}
	) : Promise<PaginationData<DepositEntity>>
	{
		return this.paginationRequest<DepositEntity>('/deposits/list', page, limit, order);
	}

	public async getWithdrawals(
		page : number,
		limit : number = 30,
		order : PaginationOrder = {
			field : 'id',
			mode : 'asc'
		}
	) : Promise<PaginationData<WithdrawalEntity>>
	{
		return this.paginationRequest<WithdrawalEntity>('/withdrawals/list', page, limit, order);
	}

	public async getReferrals(
		page : number,
		limit : number = 30,
		order : PaginationOrder = {
			field : 'id',
			mode : 'asc'
		},
		filters : PaginationFilters = {}
	) : Promise<PaginationData<BalanceHistoryEntity>>{

		return this.paginationRequest<BalanceHistoryEntity>('/balance-history/list', page, limit, order, filters);

	}

	public async getCourse() : Promise<number>
	{
		return this.request<number>('/system/course');
	}

	public async getStatistic() : Promise<MeStatisticEntity>
	{
		return this.request<MeStatisticEntity>('/profile/statistic');
	}

	public async startTrading() : Promise<void>
	{
		await this.request<void>('/trading/start');
	}

	public async stopTrading() : Promise<void>
	{
		await this.request<void>('/trading/stop');
	}

	public async changeSettings(params : Record<string, any>) : Promise<void>
	{
		await this.request<void>('/profile/change-settings', params);
	}

	public async getDepositQr() : Promise<string>
	{
		return this.request<string>('/deposits/qr');
	}

	public async scanDeposits() : Promise<void>
	{
		return this.request<void>('/deposits/scan');
	}

	public async withdrawal(amount : number, wallet : string) : Promise<void>
	{
		await this.request<void>('/withdrawals/create', {
			amount, wallet
		})
	}

	public async calculateWithdrawalCommission(amount : number) : Promise<number>
	{
		return +await this.request<number>('/withdrawals/calculate-commission', {
			amount
		})
	}

	public async validateWithdrawalAddress(wallet : string) : Promise<boolean>
	{
		return await this.request<boolean>('/withdrawals/validate-address', {
			wallet
		})
	}

	public async getTradeHistory(
		page : number,
		limit : number = 30,
		order : PaginationOrder = {
			field : 'id',
			mode : 'asc'
		},
		filters : PaginationFilters = {}
	) : Promise<PaginationData<TradeEntity>>
	{
		return this.paginationRequest<TradeEntity>('/trading/history', page, limit, order, filters);

	}

	public async getTradeStatistic(mode : string) : Promise<any>
	{
		return this.request<any>('/trading/statistic', {mode});
	}

	public async getTradeAnalytic(mode : string) : Promise<{created_at : string, value : number}[]>
	{
		return this.request<any>('/trading/analytic', {mode});
	}

}